import type { AnimationAdapter } from '../types';
import { zIndex } from '@/styles/variants';

/**
 * Real browser animation adapter
 * Provides visual effects for tutorial interactions
 */
export class BrowserAnimationAdapter implements AnimationAdapter {
  async fadeIn(element: HTMLElement, durationMs: number): Promise<void> {
    element.style.transition = `opacity ${durationMs}ms ease-in`;
    element.style.opacity = '0';

    // Trigger reflow
    void element.offsetHeight;

    element.style.opacity = '1';

    return new Promise((resolve) => {
      setTimeout(resolve, durationMs);
    });
  }

  async fadeOut(element: HTMLElement, durationMs: number): Promise<void> {
    element.style.transition = `opacity ${durationMs}ms ease-out`;
    element.style.opacity = '1';

    // Trigger reflow
    void element.offsetHeight;

    element.style.opacity = '0';

    return new Promise((resolve) => {
      setTimeout(resolve, durationMs);
    });
  }

  async slideIn(
    element: HTMLElement,
    direction: 'top' | 'bottom' | 'left' | 'right'
  ): Promise<void> {
    const durationMs = 300;
    element.style.transition = `transform ${durationMs}ms ease-out`;

    const transforms = {
      top: 'translateY(-20px)',
      bottom: 'translateY(20px)',
      left: 'translateX(-20px)',
      right: 'translateX(20px)',
    };

    element.style.transform = transforms[direction];

    // Trigger reflow
    void element.offsetHeight;

    element.style.transform = 'translate(0, 0)';

    return new Promise((resolve) => {
      setTimeout(resolve, durationMs);
    });
  }

  async highlight(element: HTMLElement, durationMs: number): Promise<void> {
    const originalBoxShadow = element.style.boxShadow;

    element.style.transition = `box-shadow ${durationMs}ms ease-in-out`;
    element.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5)';

    await new Promise((resolve) => setTimeout(resolve, durationMs));

    element.style.boxShadow = originalBoxShadow;
  }

  async rippleEffect(element: HTMLElement, color: string): Promise<void> {
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Create outer ripple (large expanding circle)
    const rippleOuter = document.createElement('div');
    rippleOuter.className = 'tutorial-ripple-outer';
    rippleOuter.style.cssText = `
      position: fixed;
      top: ${centerY}px;
      left: ${centerX}px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${color};
      pointer-events: none;
      z-index: ${zIndex.tutorialRipple};
      transform: translate(-50%, -50%);
      animation: tutorial-ripple-expand 1s ease-out;
    `;

    // Create inner pulse (pulsing ring)
    const rippleInner = document.createElement('div');
    rippleInner.className = 'tutorial-ripple-inner';
    rippleInner.style.cssText = `
      position: fixed;
      top: ${centerY}px;
      left: ${centerX}px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 4px solid ${color};
      background: transparent;
      pointer-events: none;
      z-index: ${zIndex.tutorialRipple + 1};
      transform: translate(-50%, -50%);
      animation: tutorial-ripple-pulse 1s ease-in-out;
    `;

    // Inject CSS if not already present
    if (!document.getElementById('tutorial-ripple-styles')) {
      const style = document.createElement('style');
      style.id = 'tutorial-ripple-styles';
      style.textContent = `
        @keyframes tutorial-ripple-expand {
          0% {
            width: 40px;
            height: 40px;
            opacity: 0.8;
          }
          50% {
            width: 120px;
            height: 120px;
            opacity: 0.4;
          }
          100% {
            width: 200px;
            height: 200px;
            opacity: 0;
          }
        }

        @keyframes tutorial-ripple-pulse {
          0% {
            width: 60px;
            height: 60px;
            opacity: 1;
            border-width: 4px;
          }
          50% {
            width: 100px;
            height: 100px;
            opacity: 0.6;
            border-width: 3px;
          }
          100% {
            width: 140px;
            height: 140px;
            opacity: 0;
            border-width: 2px;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(rippleOuter);
    document.body.appendChild(rippleInner);

    // Wait for animation and cleanup
    await new Promise((resolve) => setTimeout(resolve, 1000));
    rippleOuter.remove();
    rippleInner.remove();
  }
}

/**
 * No-op animation adapter for testing
 * Completes immediately without visual effects
 */
export class NoOpAnimationAdapter implements AnimationAdapter {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fadeIn(_element: HTMLElement, _durationMs: number): Promise<void> {
    // No-op
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fadeOut(_element: HTMLElement, _durationMs: number): Promise<void> {
    // No-op
  }

  async slideIn(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _element: HTMLElement,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _direction: 'top' | 'bottom' | 'left' | 'right'
  ): Promise<void> {
    // No-op
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async highlight(_element: HTMLElement, _durationMs: number): Promise<void> {
    // No-op
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async rippleEffect(_element: HTMLElement, _color: string): Promise<void> {
    // No-op
  }
}
